import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent } from '../atoms/dialog';
import { DropdownMenuSeparator } from '../atoms/dropdown-menu';
import { Button } from '../atoms/button';
import { googleDriveService } from '@ideoz/core/api/services/googleDrive.service';
import Icon from '../molecules/Icon';
import DriveEmptyState from '../molecules/DriveEmptyState';
import { X } from 'lucide-react';
import { ApiUrls } from '@ideoz/core';

const googleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M15.0017 8.15571C15.0017 7.58016 14.954 7.16016 14.8509 6.72461H8.14453V9.32236H12.081C12.0017 9.96793 11.5731 10.9402 10.6207 11.5935L10.6074 11.6804L12.7278 13.2902L12.8747 13.3046C14.2239 12.0835 15.0017 10.2868 15.0017 8.15571Z"
      fill="#4285F4"
    />
    <path
      d="M8.14266 15.0004C10.0712 15.0004 11.6902 14.3781 12.8728 13.3048L10.6188 11.5936C10.0157 12.0059 9.20613 12.2936 8.14266 12.2936C6.25379 12.2936 4.65064 11.0726 4.07916 9.38477L3.99539 9.39174L1.79055 11.064L1.76172 11.1425C2.93631 13.4292 5.349 15.0004 8.14266 15.0004Z"
      fill="#34A853"
    />
    <path
      d="M4.07933 9.38497C3.92854 8.94942 3.84127 8.48272 3.84127 8.00052C3.84127 7.51827 3.92854 7.05162 4.0714 6.61607L4.0674 6.52331L1.83493 4.82422L1.76189 4.85827C1.27778 5.80717 1 6.87274 1 8.00052C1 9.12829 1.27778 10.1938 1.76189 11.1427L4.07933 9.38497Z"
      fill="#FBBC05"
    />
    <path
      d="M8.1427 3.70665C9.48395 3.70665 10.3887 4.27442 10.9046 4.7489L12.9205 2.82C11.6824 1.69222 10.0712 1 8.1427 1C5.34902 1 2.93631 2.5711 1.76172 4.85775L4.07124 6.61555C4.65066 4.92777 6.25382 3.70665 8.1427 3.70665Z"
      fill="#EB4335"
    />
  </svg>
);

const loadingSpinner = () => (
  <svg
    className="svg-spinner"
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M10 1.875V3.95833M10 15V18.3333M4.79167 10H1.875M17.7083 10H16.4583M15.3809 15.3809L14.7917 14.7917M15.5535 4.51316L14.375 5.69167M4.10131 15.8987L6.45833 13.5417M4.2739 4.34057L6.04167 6.10833"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type FolderItem = { name: string; path?: string; folders?: number; starred?: boolean };

type GoogleFolderDialogProps = {
  open?: boolean;
  onClose?: () => void;
  step?: number;
  selectedFolder?: string | null;
  onStepChange?: (step: number) => void;
  onSelectFolder?: (name: string) => void;
  onAllowAccess?: (folderId: string) => void;
  folders?: FolderItem[];
};

const GoogleFolderDialog: React.FC<GoogleFolderDialogProps> = ({
  open = false,
  onClose = () => {},
  step = 1,
  selectedFolder = null,
  onStepChange = () => {},
  onSelectFolder = () => {},
  onAllowAccess = () => {},
  folders = [],
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [driveFolders, setDriveFolders] = useState<any[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string>('root');
  const [currentFolderData, setCurrentFolderData] = useState<any>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolderName, setSelectedFolderName] = useState<string | null>(null);
  const [folderHistory, setFolderHistory] = useState<Array<{ id: string; name: string }>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const oauthWindowRef = useRef<Window | null>(null);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check Google Drive authentication status when dialog opens
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (!open) return;

      setIsCheckingAuth(true);
      try {
        console.log('🔍 Checking Google Drive auth status...');
        const response = await googleDriveService.checkAuthStatus();
        console.log('✅ Auth status:', response);
        setIsAuthenticated(response.authenticated);

        // If authenticated and we're on step 2, automatically move to step 3 and fetch folders
        if (response.authenticated && step === 2) {
          console.log('✅ Already authenticated! Moving to folder selection...');
          onStepChange(3);
          fetchFolders('root');
        } else if (response.authenticated && step === 3) {
          // If we're already on step 3 and authenticated, fetch folders
          fetchFolders(currentFolderId);
        }
      } catch (error) {
        console.error('❌ Error checking auth status:', error);
        console.warn(
          '⚠️ Backend /gdrive/auth/status endpoint not working. Assuming not authenticated.'
        );
        setIsAuthenticated(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthStatus();
  }, [open, step, onStepChange]);

  // Fetch folders from Google Drive
  const fetchFolders = async (folderId: string, addToHistory = false) => {
    setLoadingFolders(true);
    try {
      console.log(`📂 Fetching folders for: ${folderId}`);
      const response = await googleDriveService.getFolders(folderId);
      console.log('✅ Folders response:', response);

      setDriveFolders(response.folders || []);
      setCurrentFolderId(folderId);
      setCurrentFolderData(response);

      // Add to history when navigating into a folder
      if (addToHistory && response.name) {
        setFolderHistory((prev) => [...prev, { id: folderId, name: response.name ?? '' }]);
      }
    } catch (error) {
      console.error('❌ Error fetching folders:', error);
    } finally {
      setLoadingFolders(false);
    }
  };

  // Handle OAuth popup window
  const handleGoogleAuth = async () => {
    setIsAuthenticating(true);
    try {
      // Get OAuth URL from backend
      const response = await googleDriveService.getAuthUrl(
        ApiUrls.platformLink + 'oauth2-callback'
      );

      if (!response.url) {
        throw new Error('No OAuth URL received');
      }

      console.log('🔗 Opening OAuth in new tab:', response.url);

      // Open OAuth in new tab
      const width = 600;
      const height = 700;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;

      oauthWindowRef.current = window.open(
        response.url,
        'GoogleDriveOAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
      );

      // Start checking for OAuth completion
      startOAuthCheck();
    } catch (error) {
      console.error('❌ Error starting OAuth:', error);
      setIsAuthenticating(false);
    }
  };

  // Start polling to check if OAuth is complete
  const startOAuthCheck = () => {
    let authCode: string | null = null;

    checkIntervalRef.current = setInterval(async () => {
      if (!oauthWindowRef.current) return;

      try {
        // Try to access popup URL to extract code (only works if same origin)
        if (!oauthWindowRef.current.closed && oauthWindowRef.current.location) {
          console.log('🔍 Checking OAuth popup URL...', oauthWindowRef.current.location.href);
          const popupUrl = oauthWindowRef.current.location.href;
          if (popupUrl.includes('code=')) {
            const urlParams = new URLSearchParams(popupUrl.split('?')[1]);
            console.log('🔍 Extracted URL parameters:', urlParams.toString());
            authCode = urlParams.get('code');
            console.log('✅ Extracted authorization code from popup');
          }
        }
      } catch (e) {
        // Cross-origin access blocked, that's okay
      }

      // Check if popup is closed
      if (oauthWindowRef.current.closed) {
        console.log('🔍 OAuth window closed');
        stopOAuthCheck();

        if (authCode) {
          // Send code to backend
          try {
            console.log('🔄 Sending authorization code to backend...');
            const redirectUrl = ApiUrls.platformLink + 'oauth2-callback';
            const response: any = await googleDriveService.completeOAuthCallback(
              authCode,
              redirectUrl
            );

            if (response.userId && response.message === 'Authentication successful') {
              console.log('✅ Authentication successful!');
              setIsAuthenticating(false);
              onStepChange(3);
              fetchFolders('root');
            } else {
              console.log('❌ Authentication failed');
              setIsAuthenticating(false);
            }
          } catch (error) {
            console.error('❌ Error sending code to backend:', error);
            setIsAuthenticating(false);
          }
        } else {
          // No code extracted, user may have cancelled
          console.log('❌ No authorization code received - user may have cancelled');
          setIsAuthenticating(false);
        }
      }
    }, 1000); // Check every second
  };

  // Stop OAuth checking
  const stopOAuthCheck = () => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopOAuthCheck();
      if (oauthWindowRef.current && !oauthWindowRef.current.closed) {
        oauthWindowRef.current.close();
      }
    };
  }, []);

  // Handle folder selection (clicking on folder name/icon)
  const handleFolderSelect = (folder: any) => {
    const { id: folderId, name: folderName, folderCount } = folder;

    // Toggle selection for folders with subfolders
    if (selectedFolderId === folderId) {
      setSelectedFolderId(null);
      setSelectedFolderName(null);
      setShowEmptyState(false);
    } else {
      setSelectedFolderId(folderId);
      setSelectedFolderName(folderName);
      setShowEmptyState(false);
    }
  };

  // Handle folder navigation (clicking on arrow)
  const handleFolderNavigate = (folder: any) => {
    console.log('📁 Navigating into folder:', folder);
    fetchFolders(folder.id, true);
    setSelectedFolderId(null); // Clear selection when navigating
    setSelectedFolderName(null); // Clear folder name when navigating
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    if (currentFolderData?.parentId) {
      // Navigate to parent folder
      fetchFolders(currentFolderData.parentId);
      // Remove last item from history
      setFolderHistory((prev) => prev.slice(0, -1));
    } else {
      // Navigate to root
      fetchFolders('root');
      setFolderHistory([]);
    }
    setSelectedFolderId(null);
    setSelectedFolderName(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="sm:max-w-lg gap-4 max-h-[90vh] overflow-auto">
        <button onClick={onClose} className="absolute top-4 right-4 z-10">
          <Icon icon={X} color="#667085" strokeWidth={2} width={20} height={20} />
        </button>
        {step === 1 ? (
          /* Step 1: Introduction - Keep existing code */
          <>
            {/* Cloud Icon */}
            <div className="flex justify-center">
              <div className="w-8 h-8 bg-[#F0F9FF] rounded-sm flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  x="0px"
                  y="0px"
                  width="30"
                  height="30"
                  viewBox="0 0 48 48"
                >
                  <path fill="#FFC107" d="M17 6L31 6 45 30 31 30z"></path>
                  <path fill="#1976D2" d="M9.875 42L16.938 30 45 30 38 42z"></path>
                  <path fill="#4CAF50" d="M3 30.125L9.875 42 24 18 17 6z"></path>
                </svg>
              </div>
            </div>

            {/* Header */}
            <div className="text-center">
              <h2 className="text-base font-semibold text-gray-800 mb-2">
                Connect Your Google Drive Folder
              </h2>
              <p className="text-sm text-gray-800">
                Link the project google drive folder to keep your Ideoz project up to date
                automatically with no manual uploads.
              </p>
            </div>

            <DropdownMenuSeparator className="bg-[#F5F5F5] my-0 mx-0" />

            {/* Features List */}
            <div className="space-y-4 bg-background p-4 rounded-lg">
              {/* Auto-Sync */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M11.3333 0.666992L14 3.33366M14 3.33366L11.3333 6.00033M14 3.33366H4.66667C3.95942 3.33366 3.28115 3.61461 2.78105 4.11471C2.28095 4.6148 2 5.29308 2 6.00033V7.33366M4.66667 15.3337L2 12.667M2 12.667L4.66667 10.0003M2 12.667H11.3333C12.0406 12.667 12.7189 12.386 13.219 11.8859C13.719 11.3858 14 10.7076 14 10.0003V8.66699"
                      stroke="#DC6803"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-600 mb-0">Auto-Sync</h3>
                  <p className="text-sm text-gray-800">
                    Changes in your Drive folder automatically reflect with no manual uploads.
                  </p>
                </div>
              </div>

              {/* AI Context */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M11.3333 0.666992L14 3.33366M14 3.33366L11.3333 6.00033M14 3.33366H4.66667C3.95942 3.33366 3.28115 3.61461 2.78105 4.11471C2.28095 4.6148 2 5.29308 2 6.00033V7.33366M4.66667 15.3337L2 12.667M2 12.667L4.66667 10.0003M2 12.667H11.3333C12.0406 12.667 12.7189 12.386 13.219 11.8859C13.719 11.3858 14 10.7076 14 10.0003V8.66699"
                      stroke="#0086C9"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-600 mb-0">AI Context</h3>
                  <p className="text-sm text-gray-800">
                    Our AI understands your files to provide smarter insights and suggestions.
                  </p>
                </div>
              </div>

              {/* Secure Access */}
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M11.3333 0.666992L14 3.33366M14 3.33366L11.3333 6.00033M14 3.33366H4.66667C3.95942 3.33366 3.28115 3.61461 2.78105 4.11471C2.28095 4.6148 2 5.29308 2 6.00033V7.33366M4.66667 15.3337L2 12.667M2 12.667L4.66667 10.0003M2 12.667H11.3333C12.0406 12.667 12.7189 12.386 13.219 11.8859C13.719 11.3858 14 10.7076 14 10.0003V8.66699"
                      stroke="#079455"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-600 mb-0">Secure Access</h3>
                  <p className="text-sm text-gray-800">
                    Only the folder you choose is accessible—your privacy is protected.
                  </p>
                </div>
              </div>
            </div>

            <DropdownMenuSeparator className="bg-[#F5F5F5] my-0 mx-0" />

            {/* How it works collapsible */}
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer text-base font-semibold text-gray-900">
                How it works?
                <svg
                  className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </summary>

              {/* Items collapsible */}
              <div className="mt-4 pb-3 space-y-3 bg-background p-4 rounded-lg">
                <div className="flex gap-3">
                  <span className="text-sm text-gray-800 flex w-6 h-6 bg-primary-100 rounded-sm items-center justify-center">
                    1
                  </span>
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-600 mb-0">
                      Authenticate with Google
                    </h4>
                    <p className="text-sm text-gray-800 mb-0">
                      Sign in securely using your Google account—Ideoz never sees your password.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="text-sm text-gray-800 flex w-6 h-6 bg-primary-100 rounded-sm items-center justify-center">
                    2
                  </span>
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-600 mb-0">Choose a folder</h4>
                    <p className="text-sm text-gray-800 mb-0">
                      Select which Drive folder to link. Only that folder (and its subfolders) will
                      sync.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="text-sm text-gray-800 flex w-6 h-6 bg-primary-100 rounded-sm items-center justify-center">
                    3
                  </span>
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-600 mb-0">
                      Grant permissions
                    </h4>
                    <p className="text-sm text-gray-800 mb-0">
                      Allow Ideoz to view and sync files from your selected folder. You can revoke
                      access anytime.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex bg-[#ECFDF3] w-6 h-6 rounded-sm items-center justify-center flex-shrink-0">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <path
                        d="M10 3L4.5 8.5L2 6"
                        stroke="#17B26A"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-gray-600 mb-0">Start working</h4>
                    <p className="text-sm text-gray-800 mb-0">
                      Your files are now searchable in Ideoz, and new changes sync automatically.
                    </p>
                  </div>
                </div>
              </div>
            </details>

            {/* Step indicator and Get started button */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-5 h-2 bg-primary-600 rounded-full"></span>
                  <span className="w-2 h-2 bg-[#D5D7DA] rounded-full"></span>
                  <span className="w-2 h-2 bg-[#D5D7DA] rounded-full"></span>
                  <span className="w-2 h-2 bg-[#D5D7DA] rounded-full"></span>
                </div>
                <span className="text-sm text-gray-800">Step 1 of 4</span>
              </div>
              <Button variant="primary" onClick={() => onStepChange(2)}>
                Get started
              </Button>
            </div>
          </>
        ) : step === 2 ? (
          /* Step 2: Sign in with Google - NEW IMPLEMENTATION */
          <>
            {/* Shield Icon */}
            <div className="flex justify-center">
              <div className="w-8 h-8 bg-[#ECFDF3] rounded-sm flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M10.0007 18.3337C10.0007 18.3337 16.6673 15.0003 16.6673 10.0003V4.16699L10.0007 1.66699L3.33398 4.16699V10.0003C3.33398 15.0003 10.0007 18.3337 10.0007 18.3337Z"
                    stroke="#079455"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* Header */}
            <div className="text-center">
              <h2 className="text-base font-semibold text-gray-800 mb-2">Sign in with Google</h2>
              <p className="text-[12px] text-gray-800">
                Authenticate securely Ideoz never sees your password.
              </p>
            </div>

            <DropdownMenuSeparator className="bg-[#F5F5F5] my-0 mx-0" />

            {/* Continue with Google Button - NEW */}
            <div className="flex justify-center">
              <Button
                variant="outline"
                disabled={isAuthenticating}
                className="max-w-sm gap-1 border-gray-400 disabled:opacity-50 "
                onClick={handleGoogleAuth}
              >
                {isAuthenticating ? loadingSpinner() : googleIcon()}
                <span className="font-semibold text-sm">
                  {isAuthenticating ? 'Authenticating...' : 'Continue with Google'}
                </span>
              </Button>
            </div>

            {/* Privacy Message */}
            <p className="text-center text-sm text-gray-800">
              Only the folder you choose is accessible—your privacy is protected.
            </p>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
              <div className="flex items-start gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <g clip-path="url(#clip0_8193_12888)">
                    <path
                      d="M8.00065 14.6663C11.6825 14.6663 14.6673 11.6816 14.6673 7.99967C14.6673 4.31778 11.6825 1.33301 8.00065 1.33301C4.31875 1.33301 1.33398 4.31778 1.33398 7.99967C1.33398 11.6816 4.31875 14.6663 8.00065 14.6663Z"
                      stroke="#2A518C"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8 10.6663V7.99967M8 5.33301H8.00667"
                      stroke="#2A518C"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_8193_12888">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold text-xs text-blue-900 mb-0">
                    Secure authentication
                  </h3>
                  <p className=" text-xs text-blue-800 w-11/12">
                    Google handles your login directly. Ideoz only receives permission to access
                    your chosen folder.
                  </p>
                </div>
              </div>
            </div>

            {/* Step indicator and Back button */}
            <div className="flex items-center justify-between pt-4 border-t border-[#F5F5F5] ">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-blue-light-500 rounded-full"></span>
                  <span className="w-5 h-2 bg-primary-600 rounded-full"></span>
                  <span className="w-2 h-2 bg-[#D5D7DA] rounded-full"></span>
                  <span className="w-2 h-2 bg-[#D5D7DA] rounded-full"></span>
                </div>
                <span className="text-sm text-gray-800">Step 2 of 4</span>
              </div>
              <Button variant="outline" onClick={() => onStepChange(1)} disabled={isAuthenticating}>
                Back
              </Button>
            </div>
          </>
        ) : step === 3 ? (
          /* Step 3: Select folder - TODO: Will add folder navigation next */
          <>
            {/* Shield Icon */}
            <div className="flex justify-center">
              <div className="w-8 h-8 bg-[#ECFDF3] rounded-sm flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M10.0007 18.3337C10.0007 18.3337 16.6673 15.0003 16.6673 10.0003V4.16699L10.0007 1.66699L3.33398 4.16699V10.0003C3.33398 15.0003 10.0007 18.3337 10.0007 18.3337Z"
                    stroke="#079455"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </div>

            <div className="text-center">
              <h2 className="text-base font-semibold text-gray-800 mb-2">Select your folder</h2>
              <p className="text-sm text-gray-800">Choose a Google Drive Folder</p>
            </div>

            <DropdownMenuSeparator className="bg-[#F5F5F5] my-0 mx-0" />

            {/* Search Box */}
            <div>
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#717680]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-4 py-2 rounded-md border border-[#D5D7DA] bg-white shadow-[0_1px_2px_0_rgba(10,13,18,0.05)] text-[#717680] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Selected Folder Banner */}
            {selectedFolderName && (
              <div className="flex flex-row items-start gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-sm text-gray-700 font-normal">Selected Folder</span>
                  <span className="text-[#2A518C] font-semibold text-base">
                    {selectedFolderName}
                  </span>
                </div>
              </div>
            )}

            {/* Folders List */}
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <h3 className="text-sm font-semibold text-gray-800 mb-0 px-3 py-2 bg-[#F5F5F5]">
                All folders
              </h3>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {loadingFolders ? (
                  <div className="flex items-center justify-center py-8">
                    {loadingSpinner()}
                    <span className="ml-2 text-gray-600">Loading folders...</span>
                  </div>
                ) : showEmptyState ? (
                  // Show only empty state when an empty folder is selected
                  <DriveEmptyState
                    icon={
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                      >
                        <path
                          d="M29.3327 25.3333C29.3327 26.0406 29.0517 26.7189 28.5516 27.219C28.0515 27.719 27.3733 28 26.666 28H5.33268C4.62544 28 3.94716 27.719 3.44706 27.219C2.94697 26.7189 2.66602 26.0406 2.66602 25.3333V6.66667C2.66602 5.95942 2.94697 5.28115 3.44706 4.78105C3.94716 4.28095 4.62544 4 5.33268 4H11.9993L14.666 8H26.666C27.3733 8 28.0515 8.28095 28.5516 8.78105C29.0517 9.28115 29.3327 9.95942 29.3327 10.6667V25.3333Z"
                          stroke="#717680"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    }
                    title="There are no folders"
                    description="There are no folders exists here"
                  />
                ) : (
                  (() => {
                    const filteredFolders = driveFolders.filter((folder) =>
                      folder.name.toLowerCase().includes(searchQuery.toLowerCase())
                    );

                    if (filteredFolders.length === 0 && searchQuery) {
                      // No search results
                      return (
                        <DriveEmptyState
                          icon={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                              fill="none"
                            >
                              <path
                                d="M11.9993 18.6667H19.9993M29.3327 25.3333C29.3327 26.0406 29.0517 26.7189 28.5516 27.219C28.0515 27.719 27.3733 28 26.666 28H5.33268C4.62544 28 3.94716 27.719 3.44706 27.219C2.94697 26.7189 2.66602 26.0406 2.66602 25.3333V6.66667C2.66602 5.95942 2.94697 5.28115 3.44706 4.78105C3.94716 4.28095 4.62544 4 5.33268 4H11.9993L14.666 8H26.666C27.3733 8 28.0515 8.28095 28.5516 8.78105C29.0517 9.28115 29.3327 9.95942 29.3327 10.6667V25.3333Z"
                                stroke="#717680"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          }
                          title="Folder name not exist"
                          // description={`No folders match "${searchQuery}". Try a different search term.`}
                          description="Please try another name folder name"
                        />
                      );
                    } else if (filteredFolders.length === 0) {
                      // Empty folder
                      return (
                        <DriveEmptyState
                          icon={
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="32"
                              height="32"
                              viewBox="0 0 32 32"
                              fill="none"
                            >
                              <path
                                d="M29.3327 25.3333C29.3327 26.0406 29.0517 26.7189 28.5516 27.219C28.0515 27.719 27.3733 28 26.666 28H5.33268C4.62544 28 3.94716 27.719 3.44706 27.219C2.94697 26.7189 2.66602 26.0406 2.66602 25.3333V6.66667C2.66602 5.95942 2.94697 5.28115 3.44706 4.78105C3.94716 4.28095 4.62544 4 5.33268 4H11.9993L14.666 8H26.666C27.3733 8 28.0515 8.28095 28.5516 8.78105C29.0517 9.28115 29.3327 9.95942 29.3327 10.6667V25.3333Z"
                                stroke="#717680"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                          }
                          title="There are no folders"
                          description="There are no folders exists here"
                        />
                      );
                    }

                    return filteredFolders.map((folder) => {
                      const isSelected = selectedFolderId === folder.id;
                      const hasSubfolders = (folder.folderCount || 0) > 0;

                      return (
                        <div
                          key={folder.id}
                          className={`flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50 relative border-l-4 ${isSelected ? 'bg-blue-50 border-l-blue-600' : 'border-l-transparent'} `}
                        >
                          <div
                            className="flex items-center gap-3 flex-1"
                            onClick={() => handleFolderSelect(folder)}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M16.6673 14.6667C16.6673 15.0203 16.5268 15.3594 16.2768 15.6095C16.0267 15.8595 15.6876 16 15.334 16H4.66732C4.3137 16 3.97456 15.8595 3.72451 15.6095C3.47446 15.3594 3.33398 15.0203 3.33398 14.6667V5.33333C3.33398 4.97971 3.47446 4.64057 3.72451 4.39052C3.97456 4.14048 4.3137 4 4.66732 4H8.00065L9.33398 6H15.334C15.6876 6 16.0267 6.14048 16.2768 6.39052C16.5268 6.64057 16.6673 6.97971 16.6673 7.33333V14.6667Z"
                                stroke="#535862"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-gray-800'}`}
                              >
                                {folder.name}
                              </p>
                              {currentFolderData.name && folder.name && (
                                <p className="text-xs text-gray-600">
                                  {currentFolderData.name} / {folder.name}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[12px] text-blue-700 font-medium bg-blue-50 px-2 py-1 rounded-full border border-blue-200">
                              {folder.folderCount || 0} folders
                            </span>
                            <button
                              onClick={() => handleFolderNavigate(folder)}
                              className={`p-1 rounded${hasSubfolders ? ' hover:bg-gray-200' : ''}`}
                              disabled={!hasSubfolders}
                            >
                              <svg
                                className="w-5 h-5 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 5l7 7-7 7"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            </div>

            {/* Step indicator and buttons */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-blue-light-500 rounded-full"></span>
                  <span className="w-2 h-2 bg-blue-light-500 rounded-full"></span>
                  <span className="w-5 h-2 bg-primary-600 rounded-full"></span>
                  <span className="w-2 h-2 bg-[#D5D7DA] rounded-full"></span>
                </div>
                <span className="text-sm text-gray-800">Step 3 of 4</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    // If showing empty state, hide it and clear selection
                    if (showEmptyState) {
                      setShowEmptyState(false);
                      setSelectedFolderId(null);
                      setSelectedFolderName(null);
                      return;
                    }

                    // If in subfolder, go back to parent
                    if (currentFolderId !== 'root' && currentFolderData?.parentId) {
                      handleBackNavigation();
                    } else {
                      // If in root, go back to Step 2
                      onStepChange(2);
                    }
                  }}
                >
                  Back
                </Button>
                <Button
                  onClick={() => {
                    if (selectedFolderId) {
                      onStepChange(4);
                    }
                  }}
                  disabled={!selectedFolderId}
                  variant="primary"
                >
                  Select folder
                </Button>
              </div>
            </div>
          </>
        ) : step === 4 ? (
          /* Step 4: Grant Ideoz access */
          <>
            {/* Shield Icon */}
            <div className="flex justify-center">
              <div className="w-8 h-8 bg-[#FFFAEB] rounded-sm flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M10.0007 18.3337C10.0007 18.3337 16.6673 15.0003 16.6673 10.0003V4.16699L10.0007 1.66699L3.33398 4.16699V10.0003C3.33398 15.0003 10.0007 18.3337 10.0007 18.3337Z"
                    stroke="#DC6803"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* Header */}
            <div className="text-center">
              <h2 className="text-base font-semibold text-gray-800 mb-2">Grant Ideoz access</h2>
              <p className="text-sm text-gray-800">
                Review the permissions Ideoz needs to sync your folder.
              </p>
            </div>

            <DropdownMenuSeparator className="bg-[#F5F5F5] my-0 mx-0" />

            {/* Selected Folder */}
            <div className="p-2 bg-[#ECFDF3] border border-[#AAF0C4] rounded-sm">
              <div className="flex items-start gap-1">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 15 13"
                  fill="none"
                >
                  <path
                    d="M13.8333 11.1667C13.8333 11.5203 13.6929 11.8594 13.4428 12.1095C13.1928 12.3595 12.8536 12.5 12.5 12.5H1.83333C1.47971 12.5 1.14057 12.3595 0.890524 12.1095C0.640476 11.8594 0.5 11.5203 0.5 11.1667V1.83333C0.5 1.47971 0.640476 1.14057 0.890524 0.890524C1.14057 0.640476 1.47971 0.5 1.83333 0.5H5.16667L6.5 2.5H12.5C12.8536 2.5 13.1928 2.64048 13.4428 2.89052C13.6929 3.14057 13.8333 3.47971 13.8333 3.83333V11.1667Z"
                    stroke="#535862"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <div className="flex-1">
                  <p className="text-xs text-[#414651] mb-1">Selected folder</p>
                  <p className="text-lg font-semibold text-[#414651]">
                    {selectedFolderName || 'Selected Folder'}
                  </p>
                </div>
              </div>
            </div>

            {/* Permissions List */}
            <div className="space-y-3">
              {/* View and import files */}
              <div className="flex items-start gap-3">
                <div className="flex bg-[#ECFDF3] w-5 h-5 rounded-sm items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="#17B26A"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-gray-800 mb-1">View and import files</h3>
                  <p className="text-sm text-gray-800">
                    Ideoz will read files from this folder to provide AI insights and search
                    functionality.
                  </p>
                </div>
              </div>

              {/* Automatic sync */}
              <div className="flex items-start gap-3">
                <div className="flex bg-[#ECFDF3] w-5 h-5 rounded-sm items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="#17B26A"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-gray-800 mb-1">Automatic sync</h3>
                  <p className="text-sm text-gray-800">
                    New files and changes will sync automatically to keep your project up to date.
                  </p>
                </div>
              </div>

              {/* No access to other folders */}
              <div className="flex items-start gap-3">
                <div className="flex bg-[#ECFDF3] w-5 h-5 rounded-sm items-center justify-center flex-shrink-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path
                      d="M10 3L4.5 8.5L2 6"
                      stroke="#17B26A"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-gray-800 mb-1">No access to other folders</h3>
                  <p className="text-sm text-gray-800">
                    Ideoz will only access the folder you selected—not your entire Drive.
                  </p>
                </div>
              </div>
            </div>

            <DropdownMenuSeparator className="bg-[#F5F5F5] my-0 mx-0" />

            {/* How we protect your files collapsible */}
            <details className="group">
              <summary className="flex items-center justify-between cursor-pointer text-base font-semibold text-gray-900">
                How we protect your files?
                <svg
                  className="w-5 h-5 text-gray-500 transition-transform group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </summary>

              {/* Items collapsible */}
              <div className="pt-2 pb-3 space-y-3">
                <div className="flex gap-3">
                  <span className="text-sm text-gray-800 flex w-5 h-5 bg-gray-100 rounded-sm items-center justify-center">
                    1
                  </span>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-600 mb-0">
                      End-to-end encryption
                    </h4>
                    <p className="text-[10px] text-gray-800 mb-0">
                      All data transferred between Google Drive and Ideoz is encrypted using
                      industry-standard TLS/SSL protocols.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="text-sm text-gray-800 flex w-5 h-5 bg-gray-100 rounded-sm items-center justify-center">
                    2
                  </span>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-600 mb-0">Secure storage</h4>
                    <p className="text-[10px] text-gray-800 mb-0">
                      File metadata and AI-generated insights are stored securely on encrypted
                      servers. We never share your data with third parties.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <span className="text-sm text-gray-800 flex w-5 h-5 bg-gray-100 rounded-sm items-center justify-center">
                    3
                  </span>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-600 mb-0">Revoke anytime</h4>
                    <p className="text-[10px] text-gray-800 mb-0">
                      You can disconnect your Google Drive folder at any time from your project
                      settings. This immediately revokes Ideoz's access.
                    </p>
                  </div>
                </div>
              </div>
            </details>

            {/* Step indicator and buttons */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-blue-light-500 rounded-full"></span>
                  <span className="w-2 h-2 bg-blue-light-500 rounded-full"></span>
                  <span className="w-2 h-2 bg-blue-light-500 rounded-full"></span>
                  <span className="w-5 h-2 bg-primary-600 rounded-full"></span>
                </div>
                <span className="text-sm text-gray-800">Step 4 of 4</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => onStepChange(3)} disabled={isSubmitting}>
                  Back
                </Button>
                <Button
                  onClick={async () => {
                    if (selectedFolderId) {
                      setIsSubmitting(true);
                      try {
                        await onAllowAccess(selectedFolderId);
                      } finally {
                        setIsSubmitting(false);
                      }
                    }
                  }}
                  disabled={!selectedFolderId || isSubmitting}
                  className="px-3 py-2 bg-primary-600 text-white font-semibold rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      {loadingSpinner()}
                      <span className="ml-2">Connecting...</span>
                    </>
                  ) : (
                    'Allow access'
                  )}
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default GoogleFolderDialog;
